import Throbber from "@/components/throbber";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useWithdraw } from "@/hooks/use-mutations";
import { useUser } from "@/zustand/store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const withdrawSchema = z.object({
  token: z.string().min(1, "Payment Token required"),
  to: z.string().min(1, "Withdraw Address required"),
  // chain: z.string().min(1, "Chain required"),
  amount: z.string().min(1, "Amount required"),
  otp: z.string().min(1, "OTP required, check your authenticator app."),
});

const Withdraw = () => {
  const form = useForm({
    resolver: zodResolver(withdrawSchema),
  });
  const navigate = useNavigate();
  const { user } = useUser((state) => state);
  const queryClient = useQueryClient();

  const { mutateAsync: withdraw, isPending: isWithdrawing } = useWithdraw({
    onSuccess: () => {
      queryClient.invalidateQueries(["user"]);
      navigate("/dashboard/wallet");
    },
  });

  const onSubmit = (data) => {
    withdraw({ ...data, chain: "ETH" });
  };
  return (
    <div>
      {/* header */}
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="bg-primary-50 hover:bg-primary-50/90 flex size-8 items-center justify-center rounded-md"
          >
            <ArrowLeft className="size-6" />
          </button>
          <div className="ml-2 flex items-center gap-2 text-xl font-medium">
            <span>My Wallet</span>
            <span className="text-primary-50 mt-1.5">
              <ChevronRight size={16} />
            </span>
            <span>Withdrawal</span>
          </div>
        </div>
      </header>

      <div className="mx-auto mt-6 max-w-120">
        <p className="text-lg font-medium">Available balance</p>
        <p className="text-3xl leading-0 font-bold">
          {user?.balance.toFixed(2)} <span className="text-base">USD</span>
        </p>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8">
          <Form {...form}>
            <FormField
              control={form.control}
              name="token"
              render={({ field }) => (
                <FormItem className="">
                  <FormLabel>Payment Method</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className={"rounded-full bg-white"}>
                          <SelectValue placeholder="USDC" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={"USDC"}>USDC</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="to"
              render={({ field }) => (
                <FormItem className="mt-4">
                  <FormLabel>
                    Withdrawal Address{" "}
                    <span className="text-primary-50 font-normal italic">
                      (please enter the address you want your cryptocurrency
                      withdrawn to)
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter withdraw address" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem className="mt-4">
                  <FormLabel>Withdrawal Amount</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Input Amount" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="otp"
              render={({ field }) => (
                <FormItem className="mt-4">
                  <FormLabel>
                    OTP{"   "}
                    <span className="text-primary-50 font-normal italic">
                      Check your authenticator app for OTP
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Input OTP" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="mt-4 flex items-center gap-4 text-lg">
              <span className="text-primary-500/40">Withdrawal Fee</span>
              <span className="text-primary-500 font-medium">1%</span>
            </div>

            <p className="text-primary-500 mt-4 leading-4">
              <span className="font-bold">Note:</span> For your security
              withdrawals from your Zypher Wallet to your external wallet are
              processed manually and can take 24 hours. Please contact the
              support team with your account details and screenshot of your
              pending withdrawal to have it confirmed
            </p>

            <Button
              disabled={isWithdrawing}
              type="submit"
              className={"mt-6 w-full"}
            >
              {isWithdrawing ? <Throbber /> : "Withdraw"}
            </Button>
          </Form>
        </form>
      </div>
    </div>
  );
};

export default Withdraw;
